import { useState } from "react";
import { Header } from "@/components/layout/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType } from "docx";
import { saveAs } from "file-saver";
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";

type School = Database['public']['Tables']['schools']['Row'];
type AcademicYear = Database['public']['Tables']['academic_years']['Row'];
type Term = Database['public']['Tables']['terms']['Row'];

type ProfileWithSchool = {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: string;
  school_id: string | null;
  school: School | null;
};

type ReportType = "DAILY" | "WEEKLY" | "MONTHLY" | "TERMLY" | "YEARLY" | "CUSTOM";

interface PreviewData {
  totalVisits: number;
  studentVisits: {
    total: number;
    uniqueStudents: number;
    byClass: Record<string, number>;
  };
  staffVisits: {
    total: number;
    uniqueStaff: number;
    byCategory: Record<string, number>;
  };
  complaints: Record<string, number>;
  diagnoses: Record<string, number>;
  treatments: Record<string, number>;
  dateRange: { start: Date; end: Date };
  schoolInfo: { name: string; prefix: string };
}

const Reports = () => {
  const { user } = useAuth();
  const [reportType, setReportType] = useState<ReportType>("WEEKLY");
  const [selectedTerm, setSelectedTerm] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [customDateFrom, setCustomDateFrom] = useState<Date>();
  const [customDateTo, setCustomDateTo] = useState<Date>();
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<string[]>([]);
  const [includeDetailedTreatments, setIncludeDetailedTreatments] = useState(false);
  const { toast } = useToast();

  // Fetch profile with school information
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      console.log('=== FETCHING PROFILE FOR REPORTS ===');
      console.log('User from auth context:', user);

      if (!user?.schoolId) {
        throw new Error('No school ID found for user');
      }

      const { data: school, error: schoolError } = await supabase
        .from('schools')
        .select('id, name, prefix, created_at, updated_at')
        .eq('id', user.schoolId)
        .single();

      if (schoolError) {
        console.error('Error fetching school:', schoolError);
        throw schoolError;
      }

      console.log('School fetched:', school);

      const profileData = {
        id: user.profileId,
        user_id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        school_id: user.schoolId,
        school: school
      };

      console.log('Profile data constructed:', profileData);
      return profileData as ProfileWithSchool;
    },
    enabled: !!user?.schoolId
  });

  // Fetch academic years for this specific school (including global ones)
  const { data: schoolAcademicYears } = useQuery({
    queryKey: ['schoolAcademicYears', profile?.school_id],
    queryFn: async () => {
      if (!profile?.school_id) return [];
      
      console.log('Fetching academic years for school:', profile.school_id);
      const { data, error } = await supabase
        .from('academic_years')
        .select(`
          id,
          name,
          is_active,
          school_id,
          created_at,
          updated_at
        `)
        .or(`school_id.eq.${profile.school_id},school_id.is.null`)
        .order('name', { ascending: false });

      if (error) throw error;
      console.log('Academic years for school fetched:', data);
      return data;
    },
    enabled: !!profile?.school_id
  });

  // Fetch terms for this specific school (including global ones)
  const { data: schoolTerms } = useQuery({
    queryKey: ['schoolTerms', profile?.school_id],
    queryFn: async () => {
      if (!profile?.school_id || !schoolAcademicYears) return [];
      
      console.log('Fetching terms for school academic years');
      const academicYearIds = schoolAcademicYears.map(year => year.id);
      
      const { data, error } = await supabase
        .from('terms')
        .select(`
          id,
          name,
          start_date,
          end_date,
          is_active,
          academic_year_id,
          school_id
        `)
        .in('academic_year_id', academicYearIds)
        .order('start_date', { ascending: false });

      if (error) throw error;
      console.log('Terms for school fetched:', data);
      return data;
    },
    enabled: !!profile?.school_id && !!schoolAcademicYears
  });

  // Find current active period for this school
  const { data: currentPeriod } = useQuery({
    queryKey: ['currentPeriod', profile?.school_id],
    queryFn: async () => {
      if (!schoolAcademicYears || !schoolTerms) return null;
      
      console.log('Finding current period for school');
      const activeYear = schoolAcademicYears.find(year => year.is_active);
      const activeTerm = schoolTerms.find(term => term.is_active && term.academic_year_id === activeYear?.id);
      
      console.log('Current period found:', { activeYear, activeTerm });
      return {
        academicYear: activeYear,
        term: activeTerm
      };
    },
    enabled: !!schoolAcademicYears && !!schoolTerms
  });

  // Function to fetch ALL visits with pagination
  const fetchAllVisits = async (baseQuery: any) => {
    const allVisits: any[] = [];
    const pageSize = 1000;
    let currentPage = 0;
    let hasMore = true;

    console.log('=== FETCHING ALL VISITS WITH PAGINATION ===');

    while (hasMore) {
      const startRange = currentPage * pageSize;
      const endRange = startRange + pageSize - 1;
      
      console.log(`Fetching page ${currentPage + 1}: range ${startRange}-${endRange}`);

      const { data: pageData, error, count } = await baseQuery
        .range(startRange, endRange);

      if (error) {
        console.error('Error fetching visits page:', error);
        throw error;
      }

      console.log(`Page ${currentPage + 1} fetched: ${pageData?.length || 0} visits`);
      
      if (pageData && pageData.length > 0) {
        allVisits.push(...pageData);
      }

      // Check if we have more data
      hasMore = pageData && pageData.length === pageSize;
      currentPage++;

      // Safety check to prevent infinite loops
      if (currentPage > 100) {
        console.warn('Reached maximum page limit (100), stopping pagination');
        break;
      }
    }

    console.log(`=== PAGINATION COMPLETE: ${allVisits.length} total visits fetched ===`);
    return allVisits;
  };

  const getDateRange = () => {
    if (reportType === "CUSTOM") {
      if (!customDateFrom || !customDateTo) {
        throw new Error('Please select both start and end dates for custom report');
      }
      return { start: startOfDay(customDateFrom), end: endOfDay(customDateTo) };
    }

    if (reportType === "TERMLY") {
      if (!selectedTerm || !schoolTerms) {
        throw new Error('Please select a term for the termly report');
      }
      const term = schoolTerms.find(t => t.id === selectedTerm);
      if (!term) throw new Error('Selected term not found');
      return {
        start: new Date(term.start_date),
        end: new Date(term.end_date)
      };
    }

    if (reportType === "YEARLY") {
      if (!selectedYear || !schoolAcademicYears || !schoolTerms) {
        throw new Error('Please select an academic year for the yearly report');
      }
      const year = schoolAcademicYears.find(y => y.id === selectedYear);
      if (!year) throw new Error('Selected academic year not found');
      
      const yearTerms = schoolTerms.filter(t => t.academic_year_id === year.id);
      if (yearTerms.length === 0) {
        throw new Error('Selected academic year has no terms');
      }
      
      const sortedTerms = yearTerms.sort((a, b) => 
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
      return {
        start: new Date(sortedTerms[0].start_date),
        end: new Date(sortedTerms[sortedTerms.length - 1].end_date)
      };
    }

    // For DAILY, WEEKLY, MONTHLY - use ACTIVE TERM boundaries
    if (!currentPeriod?.term) {
      throw new Error('No active term found. Please ensure there is an active term for date-based reports.');
    }

    const activeTerm = currentPeriod.term;
    const termStart = new Date(activeTerm.start_date);
    const termEnd = new Date(activeTerm.end_date);
    
    console.log('=== ACTIVE TERM BASED DATE RANGE CALCULATION ===');
    console.log('Active term:', activeTerm.name);
    console.log('Term start:', termStart);
    console.log('Term end:', termEnd);
    
    // Use current date but constrain within active term
    const now = new Date();
    const currentDate = now < termStart ? termStart : (now > termEnd ? termEnd : now);
    
    console.log('Reference date (constrained to term):', currentDate);
    
    switch (reportType) {
      case "DAILY": {
        const dayStart = startOfDay(currentDate);
        const dayEnd = endOfDay(currentDate);
        
        // Ensure the day is within term boundaries
        const constrainedStart = dayStart < termStart ? termStart : dayStart;
        const constrainedEnd = dayEnd > termEnd ? termEnd : dayEnd;
        
        console.log('Daily report range:', { start: constrainedStart, end: constrainedEnd });
        return { start: constrainedStart, end: constrainedEnd };
      }
      case "WEEKLY": {
        // Calculate week boundaries but constrain to term
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // Sunday
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 }); // Saturday
        
        // Constrain week to term boundaries
        const constrainedStart = weekStart < termStart ? termStart : weekStart;
        const constrainedEnd = weekEnd > termEnd ? termEnd : weekEnd;
        
        console.log('Weekly report range (constrained to term):', { start: constrainedStart, end: constrainedEnd });
        return { start: constrainedStart, end: constrainedEnd };
      }
      case "MONTHLY": {
        // Calculate month boundaries but constrain to term
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        
        // Constrain month to term boundaries
        const constrainedStart = monthStart < termStart ? termStart : monthStart;
        const constrainedEnd = monthEnd > termEnd ? termEnd : monthEnd;
        
        console.log('Monthly report range (constrained to term):', { start: constrainedStart, end: constrainedEnd });
        return { start: constrainedStart, end: constrainedEnd };
      }
      default:
        return { start: termStart, end: termEnd };
    }
  };

  const generateReportData = async (): Promise<PreviewData | null> => {
    if (!profile?.school_id || !profile?.school) {
      toast({
        title: "Error",
        description: "No school information found for your profile",
        variant: "destructive",
      });
      return null;
    }

    console.log('=== GENERATING COMPREHENSIVE REPORT FOR SCHOOL ===');
    console.log('School ID:', profile.school_id);
    console.log('School name:', profile.school?.name);
    console.log('Report type:', reportType);

    const dateRange = getDateRange();
    console.log('Date range:', {
      start: dateRange.start.toISOString(),
      end: dateRange.end.toISOString()
    });

    // Build base query - SCHOOL-SPECIFIC ONLY
    console.log('=== BUILDING SCHOOL-SPECIFIC QUERY FOR ALL DATA ===');
    
    let baseQuery = supabase
      .from('visits')
      .select(`
        id,
        date,
        complaint,
        diagnosis,
        treatment,
        temperature,
        school_id,
        academic_year_id,
        term_id,
        student:students (
          id,
          surname,
          other_names,
          class
        ),
        community_member:community_members (
          id,
          name,
          category
        )
      `, { count: 'exact' })
      .eq('school_id', profile.school_id) // ONLY this school's visits
      .gte('date', dateRange.start.toISOString())
      .lte('date', dateRange.end.toISOString());

    // Add additional filters only if they're selected and valid
    if (reportType === "TERMLY" && selectedTerm) {
      baseQuery = baseQuery.eq('term_id', selectedTerm);
      console.log('Adding term filter:', selectedTerm);
    } else if (reportType === "YEARLY" && selectedYear) {
      baseQuery = baseQuery.eq('academic_year_id', selectedYear);
      console.log('Adding academic year filter:', selectedYear);
    } else if (["DAILY", "WEEKLY", "MONTHLY"].includes(reportType) && currentPeriod?.term) {
      // For date-based reports, filter by active term
      baseQuery = baseQuery.eq('term_id', currentPeriod.term.id);
      console.log('Adding active term filter for date-based report:', currentPeriod.term.id);
    }

    console.log('=== EXECUTING COMPREHENSIVE SCHOOL-SPECIFIC QUERY ===');
    
    try {
      // Use pagination to get ALL visits
      const visits = await fetchAllVisits(baseQuery);

      console.log('=== COMPREHENSIVE SCHOOL-SPECIFIC QUERY RESULTS ===');
      console.log(`Total visits for ${profile.school.name} (${profile.school.prefix}):`, visits?.length || 0);
      console.log('Date range used:', dateRange);
      console.log('First few visits:', visits?.slice(0, 3));

      const studentVisits = visits?.filter(v => v.student) || [];
      const staffVisits = visits?.filter(v => v.community_member) || [];

      console.log(`Student visits for ${profile.school.name}:`, studentVisits.length);
      console.log(`Staff visits for ${profile.school.name}:`, staffVisits.length);

      const visitsByClass = studentVisits.reduce((acc: Record<string, number>, visit) => {
        const className = visit.student?.class || 'Unknown';
        acc[className] = (acc[className] || 0) + 1;
        return acc;
      }, {});

      const visitsByCategory = staffVisits.reduce((acc: Record<string, number>, visit) => {
        const category = visit.community_member?.category || 'Unknown';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});

      const complaints = (visits || []).reduce((acc: Record<string, number>, visit) => {
        acc[visit.complaint] = (acc[visit.complaint] || 0) + 1;
        return acc;
      }, {});

      const diagnoses = (visits || []).reduce((acc: Record<string, number>, visit) => {
        acc[visit.diagnosis] = (acc[visit.diagnosis] || 0) + 1;
        return acc;
      }, {});

      const treatments = (visits || []).reduce((acc: Record<string, number>, visit) => {
        acc[visit.treatment] = (acc[visit.treatment] || 0) + 1;
        return acc;
      }, {});

      const reportData = {
        totalVisits: visits?.length || 0,
        studentVisits: {
          total: studentVisits.length,
          uniqueStudents: new Set(studentVisits.map(v => v.student?.id)).size,
          byClass: visitsByClass
        },
        staffVisits: {
          total: staffVisits.length,
          uniqueStaff: new Set(staffVisits.map(v => v.community_member?.id)).size,
          byCategory: visitsByCategory
        },
        complaints,
        diagnoses,
        treatments,
        dateRange,
        schoolInfo: {
          name: profile.school.name,
          prefix: profile.school.prefix
        }
      };

      console.log('=== FINAL COMPREHENSIVE SCHOOL-SPECIFIC REPORT DATA ===');
      console.log(`Report for ${profile.school.name} - Total visits:`, reportData.totalVisits);
      console.log('Student visits breakdown:', reportData.studentVisits);
      console.log('Staff visits breakdown:', reportData.staffVisits);

      return reportData;
    } catch (error: any) {
      console.error('Error in generateReportData:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }
  };

  const handlePreview = async () => {
    try {
      const data = await generateReportData();
      setPreviewData(data);
      setShowPreview(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const generateWordDocument = async () => {
    try {
      const reportData = await generateReportData();
      if (!reportData) {
        toast({
          title: "Error",
          description: "Could not generate report data",
          variant: "destructive",
        });
        return;
      }

      const dateRangeText = reportType === "DAILY" 
        ? `for ${format(reportData.dateRange.start, 'PPP')}`
        : `from ${format(reportData.dateRange.start, 'PPP')} to ${format(reportData.dateRange.end, 'PPP')}`;

      const sections = [
        new Paragraph({
          text: `${reportData.schoolInfo.name} - ${reportData.schoolInfo.prefix}`,
          heading: HeadingLevel.TITLE,
          spacing: { after: 400 },
        }),
        new Paragraph({
          text: `${reportType} HEALTH REPORT ${dateRangeText}`,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 },
        }),
        new Paragraph({
          text: `Generated on: ${format(new Date(), 'PPP')}`,
          spacing: { after: 400 },
        }),
        new Paragraph({
          text: "Visit Statistics",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Total Visits: `, bold: true }),
            new TextRun({ text: `${reportData.totalVisits}` }),
          ]
        }),
        new Paragraph({
          text: "Student Visits",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Total Visits: `, bold: true }),
            new TextRun({ text: `${reportData.studentVisits.total}` }),
          ]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Unique Students: `, bold: true }),
            new TextRun({ text: `${reportData.studentVisits.uniqueStudents}` }),
          ],
          spacing: { after: 200 }
        }),
        ...Object.entries(reportData.studentVisits.byClass).map(([className, count]) => 
          new Paragraph({
            children: [
              new TextRun({ text: `${className}: ` }),
              new TextRun({ text: `${count} visits` })
            ]
          })
        ),
        new Paragraph({
          text: "Staff Visits",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Total Visits: `, bold: true }),
            new TextRun({ text: `${reportData.staffVisits.total}` }),
          ]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Unique Staff: `, bold: true }),
            new TextRun({ text: `${reportData.staffVisits.uniqueStaff}` }),
          ],
          spacing: { after: 200 }
        }),
        ...Object.entries(reportData.staffVisits.byCategory).map(([category, count]) => 
          new Paragraph({
            children: [
              new TextRun({ text: `${category.replace(/_/g, ' ')}: ` }),
              new TextRun({ text: `${count} visits` })
            ]
          })
        ),
        new Paragraph({
          text: "Common Complaints",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        ...Object.entries(reportData.complaints)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([complaint, count], index) => 
            new Paragraph({
              children: [
                new TextRun({ text: `${complaint}: ` }),
                new TextRun({ text: `${count} occurrences` }),
                index === 0 ? new TextRun({ text: ` (Highest)`, bold: true }) : new TextRun({ text: `` })
              ]
            })
          ),
        new Paragraph({
          text: "Diagnoses",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        ...Object.entries(reportData.diagnoses)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([diagnosis, count], index) => 
            new Paragraph({
              children: [
                new TextRun({ text: `${diagnosis}: ` }),
                new TextRun({ text: `${count} cases` }),
                index === 0 ? new TextRun({ text: ` (Highest)`, bold: true }) : new TextRun({ text: `` })
              ]
            })
          ),
      ];

      // Add detailed diagnosis tables for selected diagnoses
      if (selectedDiagnoses.length > 0) {
        sections.push(
          new Paragraph({
            text: "Detailed Diagnosis Reports",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        for (const diagnosis of selectedDiagnoses) {
          const dateRange = getDateRange();
          
          // Fetch detailed visit data for this diagnosis - SCHOOL-SPECIFIC WITH PAGINATION
          let detailQuery = supabase
            .from('visits')
            .select(`
              id,
              date,
              student:students (
                surname,
                other_names,
                class
              )
            `)
            .eq('school_id', profile.school_id) // ONLY this school
            .eq('diagnosis', diagnosis)
            .gte('date', dateRange.start.toISOString())
            .lte('date', dateRange.end.toISOString());

          if (reportType === "TERMLY" && selectedTerm) {
            detailQuery = detailQuery.eq('term_id', selectedTerm);
          } else if (reportType === "YEARLY" && selectedYear) {
            detailQuery = detailQuery.eq('academic_year_id', selectedYear);
          } else if (["DAILY", "WEEKLY", "MONTHLY"].includes(reportType) && currentPeriod?.term) {
            detailQuery = detailQuery.eq('term_id', currentPeriod.term.id);
          }

          // Use pagination for detailed diagnosis data
          const diagnosisVisits = await fetchAllVisits(detailQuery);

          if (diagnosisVisits && diagnosisVisits.length > 0) {
            sections.push(
              new Paragraph({
                text: `${diagnosis} - Detailed Report (${diagnosisVisits.length} cases)`,
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              })
            );

            const table = new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1 },
                bottom: { style: BorderStyle.SINGLE, size: 1 },
                left: { style: BorderStyle.SINGLE, size: 1 },
                right: { style: BorderStyle.SINGLE, size: 1 },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                insideVertical: { style: BorderStyle.SINGLE, size: 1 },
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Student Name", bold: true })] })],
                      width: { size: 40, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Class", bold: true })] })],
                      width: { size: 30, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Date", bold: true })] })],
                      width: { size: 30, type: WidthType.PERCENTAGE },
                    }),
                  ],
                }),
                ...diagnosisVisits.map(visit => 
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ 
                          children: [new TextRun({ 
                            text: `${visit.student?.surname || 'N/A'}${visit.student?.other_names ? `, ${visit.student.other_names}` : ''}` 
                          })] 
                        })],
                      }),
                      new TableCell({
                        children: [new Paragraph({ 
                          children: [new TextRun({ text: visit.student?.class || 'N/A' })] 
                        })],
                      }),
                      new TableCell({
                        children: [new Paragraph({ 
                          children: [new TextRun({ text: format(new Date(visit.date), 'PPP') })] 
                        })],
                      }),
                    ],
                  })
                ),
              ],
            });

            sections.push(table as any);
          }
        }
      }

      // Add treatments section if selected
      if (includeDetailedTreatments) {
        sections.push(
          new Paragraph({
            text: "Treatments",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          ...Object.entries(reportData.treatments)
            .sort(([,a], [,b]) => b - a)
            .map(([treatment, count]) => 
              new Paragraph({
                children: [
                  new TextRun({ text: `${treatment}: ` }),
                  new TextRun({ text: `${count} times` })
                ]
              })
            )
        );
      }

      const doc = new Document({
        sections: [{
          properties: {},
          children: sections,
        }],
      });

      const fileName = `${reportData.schoolInfo.prefix}_health_report_${format(reportData.dateRange.start, 'yyyy-MM-dd')}_to_${format(reportData.dateRange.end, 'yyyy-MM-dd')}.docx`;
      const blob = await Packer.toBlob(doc);
      saveAs(blob, fileName);
      
      toast({
        title: "Success",
        description: `Comprehensive report generated for ${reportData.schoolInfo.name} with ${reportData.totalVisits} total visits (ALL DATA INCLUDED)`,
      });
    } catch (error: any) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate report",
        variant: "destructive",
      });
    }
  };

  const getHighestItem = (items: Record<string, number>) => {
    const sorted = Object.entries(items).sort(([,a], [,b]) => b - a);
    return sorted[0] || ["None", 0];
  };

  const getTermsForSelection = () => {
    if (reportType === "TERMLY" && schoolTerms && currentPeriod?.academicYear) {
      const terms = schoolTerms.filter(term => term.academic_year_id === currentPeriod.academicYear.id);
      console.log('Terms for current academic year:', terms);
      return terms;
    }
    return [];
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto p-6 pt-[160px] space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Reports</h1>
            {profile?.school && (
              <p className="text-sm text-muted-foreground mt-1">
                Generating COMPREHENSIVE reports for: {profile.school.name} ({profile.school.prefix}) - ALL DATA INCLUDED
                {currentPeriod?.term && ` | Active Term: ${currentPeriod.term.name}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Select
              value={reportType}
              onValueChange={(value) => {
                setReportType(value as ReportType);
                setShowPreview(false);
                setSelectedTerm("");
                setSelectedYear("");
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select report type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DAILY">Daily Report</SelectItem>
                <SelectItem value="WEEKLY">Weekly Report</SelectItem>
                <SelectItem value="MONTHLY">Monthly Report</SelectItem>
                <SelectItem value="TERMLY">Term Report</SelectItem>
                <SelectItem value="YEARLY">Year Report</SelectItem>
                <SelectItem value="CUSTOM">Custom Report</SelectItem>
              </SelectContent>
            </Select>
            
            {reportType === "TERMLY" && (
              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select term" />
                </SelectTrigger>
                <SelectContent>
                  {getTermsForSelection().map((term) => (
                    <SelectItem key={term.id} value={term.id}>
                      {term.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {reportType === "YEARLY" && schoolAcademicYears && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {schoolAcademicYears.map((year) => (
                    <SelectItem key={year.id} value={year.id}>
                      {year.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button onClick={handlePreview} variant="outline">
              Preview
            </Button>
            <Button onClick={generateWordDocument}>
              Generate Report
            </Button>
          </div>
        </div>

        {reportType === "CUSTOM" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Custom Date Range</h3>
            <div className="flex gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">From Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[240px] justify-start text-left font-normal",
                        !customDateFrom && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateFrom ? format(customDateFrom, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDateFrom}
                      onSelect={setCustomDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">To Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[240px] justify-start text-left font-normal",
                        !customDateTo && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateTo ? format(customDateTo, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDateTo}
                      onSelect={setCustomDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </Card>
        )}

        {showPreview && previewData && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              COMPREHENSIVE Report Preview - {previewData.schoolInfo.name} ({previewData.schoolInfo.prefix}) - ALL DATA INCLUDED
              {currentPeriod?.term && ` | Active Term: ${currentPeriod.term.name}`}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800">Visit Statistics</h4>
                <p className="text-2xl font-bold text-blue-600">{previewData.totalVisits}</p>
                <p className="text-sm text-blue-600">Total Visits (ALL DATA INCLUDED)</p>
                <p className="text-xs text-blue-500 mt-1">
                  Date Range: {format(previewData.dateRange.start, 'PP')} - {format(previewData.dateRange.end, 'PP')}
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800">Common Complaints</h4>
                <p className="text-lg font-bold text-green-600">
                  {getHighestItem(previewData.complaints)[0]}
                </p>
                <p className="text-sm text-green-600">
                  {getHighestItem(previewData.complaints)[1]} occurrences (Highest)
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-800">Diagnoses</h4>
                <p className="text-lg font-bold text-purple-600">
                  {getHighestItem(previewData.diagnoses)[0]}
                </p>
                <p className="text-sm text-purple-600">
                  {getHighestItem(previewData.diagnoses)[1]} cases (Highest)
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold mb-3">Select Diagnoses for Detailed Report:</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {Object.entries(previewData.diagnoses).map(([diagnosis, count]) => (
                  <div key={diagnosis} className="flex items-center space-x-2">
                    <Checkbox
                      id={diagnosis}
                      checked={selectedDiagnoses.includes(diagnosis)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDiagnoses([...selectedDiagnoses, diagnosis]);
                        } else {
                          setSelectedDiagnoses(selectedDiagnoses.filter(d => d !== diagnosis));
                        }
                      }}
                    />
                    <label htmlFor={diagnosis} className="text-sm cursor-pointer">
                      {diagnosis} ({count})
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include-treatments"
                  checked={includeDetailedTreatments}
                  onCheckedChange={(checked) => setIncludeDetailedTreatments(!!checked)}
                />
                <label htmlFor="include-treatments" className="text-sm cursor-pointer">
                  Include detailed treatments in report
                </label>
              </div>
            </div>
          </Card>
        )}
        
        <Card className="p-6">
          <p className="text-muted-foreground">
            Select a report type and click Preview to see ALL school-specific data with COMPREHENSIVE COVERAGE, then Generate Report to download a Word document.
            {currentPeriod?.term && (
              <span className="block mt-2">
                <strong>Active Term:</strong> {currentPeriod.term.name} ({format(new Date(currentPeriod.term.start_date), 'PP')} - {format(new Date(currentPeriod.term.end_date), 'PP')})
                <br />
                <em>Note: Daily, Weekly, and Monthly reports are constrained to the active term period.</em>
              </span>
            )}
            {currentPeriod?.academicYear && (
              <span className="block mt-1">
                <strong>Active Academic Year:</strong> {currentPeriod.academicYear.name}
              </span>
            )}
          </p>
        </Card>
      </main>
    </div>
  );
};

export default Reports;
